import { QueryStringInput } from '@hello-aria/query-string'
import { isEmpty, isNil, sortBy } from 'lodash'
import {
    Company,
    CompanyPerCurrencyConfig,
    PaymentTermOption,
    PaymentTermOptionType,
    UpdateCompany,
    UpdateCompanyGateway
} from '../models/companyModel'
import { resolveApiUrl } from '../utils/requestsUtils'
import { RequestResponse, requestService } from './requestServices'
import { trackEvent } from '../utils/analyticsUtils'

export const getCompaniesService = async (
    query?: QueryStringInput
): Promise<RequestResponse<Company[]> | undefined> => {
    const url = resolveApiUrl('company.getAll', { query: { ...query, params: { include: ['config'] } } })
    return requestService<Company[]>(url)
}

export function sanitizePeriodTermsOption(paymentTermOptionType: PaymentTermOptionType | null | undefined): number[] {
    if (isNil(paymentTermOptionType) || !paymentTermOptionType.enabled) return []

    if (paymentTermOptionType.enum && paymentTermOptionType.enum.length > 0) return sortBy(paymentTermOptionType.enum)

    if (paymentTermOptionType.min && paymentTermOptionType.max)
        return sortBy([paymentTermOptionType.min, paymentTermOptionType.max])

    return []
}

export function mapCurrencyConfigs(
    currencyConfigs: CompanyPerCurrencyConfig[] | undefined
): CompanyPerCurrencyConfig[] {
    currencyConfigs?.forEach((currencyConfig) => {
        if (currencyConfig.paymentTermOptions) {
            const optionsList: PaymentTermOption[] = []
            Object.keys(currencyConfig.paymentTermOptions).forEach((option) => {
                const optionKey = option as PaymentTermOption
                const termOption = currencyConfig.paymentTermOptions?.[optionKey]

                if (termOption) {
                    termOption.enum = sanitizePeriodTermsOption(termOption)
                    if (!isEmpty(termOption.enum)) optionsList.push(optionKey)
                }
            })
            currencyConfig.availablePaymentTermOptions = optionsList
        }
    })

    return currencyConfigs ?? []
}

function mapCompany(company: Company | undefined): Company | undefined {
    if (company) {
        return {
            ...company,
            config: {
                ...company.config,
                currencyConfigs: mapCurrencyConfigs(company.config.currencyConfigs)
            }
        }
    }
    return undefined
}

export const getMyCompanyService = async (companyId: string): Promise<RequestResponse<Company> | undefined> => {
    const url = resolveApiUrl('company.get', { variables: { companyId } })
    const response = await requestService<Company>(url)

    if (isNil(response?.body)) return

    return { ...response, body: mapCompany(response.body) }
}

export const updateCompanyService = async (
    payload: Partial<UpdateCompany>,
    companyId: Company['id']
): Promise<RequestResponse<Company> | undefined> => {
    const formattedPayload: Partial<UpdateCompanyGateway> = {
        name: payload.name,
        address: payload.address,
        identifier: payload.identifier
            ? {
                  ...payload.identifier,
                  country: payload.identifier.country,
                  type: payload.identifier.type
              }
            : undefined
    }

    const url = resolveApiUrl('company.update', { variables: { companyId } })
    const response = await requestService<Company>(url, { method: 'PATCH', data: formattedPayload })

    trackEvent('Update_Company')

    return response
}
